// src/components/MsoConditionListEditor.tsx
//
// ★★ ANNEX A 22 — an editable list of conditions: each is a field, an operator and a value, with
// the field and value choices DRIVEN BY THE CLOSED VOCABULARY, so an officer can only pick what the
// engine understands. (The route validates the definition again; this makes the wrong thing hard
// to author, it is not the check.)
//
// Used for each applicability variant and for each decision row's `when`. Every control has a
// label that names its row, because a column of unlabelled selects is unusable by a screen reader.
import { useId } from 'react'
import { IconButton } from './IconButton'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { defaultCondition, parseListText, withOperator } from '../lib/mso-editor'
import {
  humaniseToken,
  MSO_CONDITION_FIELDS,
  MSO_CONDITION_FIELD_NAMES,
  MSO_FIELD_LABELS,
  MSO_OPERATORS,
  MSO_OPERATOR_LABELS,
} from '../lib/mso-vocabulary'
import type { MsoCondition, MsoScalar } from '../lib/types'

export interface ConditionListEditorProps {
  /** Names this list to assistive technology, e.g. "Variant 1" or "Row R1-PRINCIPAL". */
  readonly name: string
  readonly conditions: readonly MsoCondition[]
  readonly onChange: (next: readonly MsoCondition[]) => void
  readonly disabled?: boolean
}

const FIELDS_ALPHABETICAL = [...MSO_CONDITION_FIELD_NAMES].sort((a, b) =>
  (MSO_FIELD_LABELS[a] ?? a) < (MSO_FIELD_LABELS[b] ?? b) ? -1 : 1,
)

function ValueControl({
  id,
  label,
  condition,
  onChange,
  disabled,
}: {
  id: string
  label: string
  condition: MsoCondition
  onChange: (next: MsoCondition) => void
  disabled: boolean
}) {
  const domain = MSO_CONDITION_FIELDS[condition.field]
  if (condition.op === 'present' || condition.op === 'absent') return null

  if (condition.op === 'in') {
    const chosen = Array.isArray(condition.value) ? (condition.value as readonly MsoScalar[]) : []
    if (Array.isArray(domain)) {
      return (
        <fieldset className="border-0 p-0 m-0 flex flex-wrap gap-3" disabled={disabled}>
          <legend className="sr-only">{label}</legend>
          {domain.map((option) => (
            <label key={option} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={chosen.includes(option)}
                onChange={(e) =>
                  onChange({
                    ...condition,
                    value: e.target.checked ? [...chosen, option] : chosen.filter((c) => c !== option),
                  })
                }
              />
              {humaniseToken(option)}
            </label>
          ))}
        </fieldset>
      )
    }
    return (
      <input
        id={id}
        aria-label={label}
        className="pt-native-date"
        disabled={disabled}
        defaultValue={chosen.map(String).join(', ')}
        placeholder="Values separated by commas"
        onBlur={(e) => onChange({ ...condition, value: parseListText(condition.field, e.target.value) })}
      />
    )
  }

  if (Array.isArray(domain)) {
    return (
      <select
        id={id}
        aria-label={label}
        className="pt-native-date"
        disabled={disabled}
        value={typeof condition.value === 'string' ? condition.value : ''}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
      >
        {domain.map((option) => (
          <option key={option} value={option}>
            {humaniseToken(option)}
          </option>
        ))}
      </select>
    )
  }
  if (domain === 'boolean') {
    return (
      <select
        id={id}
        aria-label={label}
        className="pt-native-date"
        disabled={disabled}
        value={condition.value === false ? 'false' : 'true'}
        onChange={(e) => onChange({ ...condition, value: e.target.value === 'true' })}
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    )
  }
  return (
    <input
      id={id}
      aria-label={label}
      className="pt-native-date"
      disabled={disabled}
      value={typeof condition.value === 'string' ? condition.value : ''}
      placeholder="e.g. B1"
      onChange={(e) => onChange({ ...condition, value: e.target.value })}
    />
  )
}

export function MsoConditionListEditor({ name, conditions, onChange, disabled = false }: ConditionListEditorProps) {
  const baseId = useId()
  const replace = (index: number, next: MsoCondition) => onChange(conditions.map((c, i) => (i === index ? next : c)))

  return (
    <div className="flex flex-col gap-2" role="group" aria-label={`${name} conditions`}>
      {conditions.length === 0 && <p className="text-sm text-[var(--sgds-body-color-subtle)] m-0">No conditions: always applies.</p>}
      {conditions.map((condition, index) => {
        const at = `${name}, condition ${index + 1}`
        return (
          <div key={index} className="flex flex-wrap items-center gap-2" data-condition={index}>
            <select
              aria-label={`${at}, field`}
              className="pt-native-date"
              style={{ width: 'auto' }}
              disabled={disabled}
              value={condition.field}
              onChange={(e) => replace(index, defaultCondition(e.target.value))}
            >
              {FIELDS_ALPHABETICAL.map((f) => (
                <option key={f} value={f}>
                  {MSO_FIELD_LABELS[f] ?? humaniseToken(f)}
                </option>
              ))}
            </select>
            <select
              aria-label={`${at}, operator`}
              className="pt-native-date"
              style={{ width: 'auto' }}
              disabled={disabled}
              value={condition.op}
              onChange={(e) => replace(index, withOperator(condition, e.target.value))}
            >
              {MSO_OPERATORS.map((op) => (
                <option key={op} value={op}>
                  {MSO_OPERATOR_LABELS[op]}
                </option>
              ))}
            </select>
            <ValueControl
              key={`${condition.field}-${condition.op}`}
              id={`${baseId}-${index}`}
              label={`${at}, value`}
              condition={condition}
              disabled={disabled}
              onChange={(next) => replace(index, next)}
            />
            <IconButton glyph="trash" tone="danger" label={`Remove ${at}`} disabled={disabled} onClick={() => onChange(conditions.filter((_, i) => i !== index))} />
          </div>
        )
      })}
      <div>
        <SgdsButton
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...conditions, defaultCondition(FIELDS_ALPHABETICAL[0] ?? 'beneficiaryType')])}
          aria-label={`Add a condition to ${name}`}
        >
          Add a condition
        </SgdsButton>
      </div>
    </div>
  )
}
